import { colName, escapeXml, makeZip, workbookFiles } from './xlsxCore';

const decoder = new TextDecoder();

export type XlsxTemplateInput = {
  types: string[];
  categories: string[];
  paymentModes: string[];
  accounts: string[];
  contacts: string[];
  taxTags: string[];
  today: string;
};

export type XlsxRow = Record<string, string>;

const columns = ['Date', 'Type', 'Category', 'Amount', 'Payment Mode', 'Account', 'Tax Tag', 'Note', 'CR/DR Person'];

const escapeFormulaSheet = (name: string) => `'${name.replace(/'/g, "''")}'`;

const sheetTextCell = (col: number, row: number, value: string, style = 0) =>
  `<c r="${colName(col)}${row}" t="inlineStr"${style ? ` s="${style}"` : ''}><is><t>${escapeXml(value)}</t></is></c>`;

const sheetNumberCell = (col: number, row: number, value: number, style = 0) =>
  `<c r="${colName(col)}${row}"${style ? ` s="${style}"` : ''}><v>${Number(value || 0)}</v></c>`;

const rowXml = (row: number, values: Array<string | number>, header = false) =>
  `<row r="${row}">${values.map((value, index) => typeof value === 'number'
    ? sheetNumberCell(index, row, value, 3)
    : sheetTextCell(index, row, value, header ? 1 : 0)).join('')}</row>`;

const optionRows = (input: XlsxTemplateInput) => {
  const lists = [input.types, input.categories, input.paymentModes, input.accounts, input.taxTags, input.contacts];
  const max = Math.max(...lists.map((list) => list.length), 1);
  const rows = [rowXml(1, ['Types', 'Categories', 'Payment Modes', 'Accounts', 'Tax Tags', 'Contacts'], true)];
  for (let i = 0; i < max; i += 1) {
    rows.push(rowXml(i + 2, lists.map((list) => list[i] || '')));
  }
  return rows.join('');
};

const validationXml = (col: string, formula: string) =>
  `<dataValidation type="list" allowBlank="1" showErrorMessage="1" sqref="${col}2:${col}501"><formula1>${escapeXml(formula)}</formula1></dataValidation>`;

const buildTransactionsSheet = (input: XlsxTemplateInput) => {
  const sample = [
    [input.today, 'Expense', input.categories[0] || 'Food', 120, input.paymentModes[0] || 'UPI', input.accounts[0] || '', input.taxTags.find((x) => /personal/i.test(x)) || '', 'Sample expense', input.contacts[0] || ''],
    [input.today, 'Income', input.categories.find((x) => /salary/i.test(x)) || input.categories[0] || 'Salary', 1000, input.paymentModes[0] || 'Bank', input.accounts[0] || '', input.taxTags.find((x) => /salary/i.test(x)) || '', 'Sample income', ''],
    [input.today, 'Investment', input.categories.find((x) => /sip|invest/i.test(x)) || input.categories[0] || 'SIP', 500, input.paymentModes[0] || 'UPI', input.accounts[0] || '', input.taxTags.find((x) => /investment/i.test(x)) || '', 'Sample investment', '']
  ];
  const rows = [rowXml(1, columns, true), ...sample.map((values, index) => rowXml(index + 2, values))].join('');
  const categoryEnd = Math.max(input.categories.length + 1, 2);
  const paymentEnd = Math.max(input.paymentModes.length + 1, 2);
  const accountEnd = Math.max(input.accounts.length + 1, 2);
  const taxTagEnd = Math.max(input.taxTags.length + 1, 2);
  const contactEnd = Math.max(input.contacts.length + 1, 2);
  const validations = [
    validationXml('B', `${escapeFormulaSheet('Options')}!$A$2:$A$4`),
    validationXml('C', `${escapeFormulaSheet('Options')}!$B$2:$B$${categoryEnd}`),
    validationXml('E', `${escapeFormulaSheet('Options')}!$C$2:$C$${paymentEnd}`),
    validationXml('F', `${escapeFormulaSheet('Options')}!$D$2:$D$${accountEnd}`),
    validationXml('G', `${escapeFormulaSheet('Options')}!$E$2:$E$${taxTagEnd}`),
    validationXml('I', `${escapeFormulaSheet('Options')}!$F$2:$F$${contactEnd}`)
  ].join('');
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>
  <cols><col min="1" max="1" width="14" customWidth="1"/><col min="2" max="3" width="18" customWidth="1"/><col min="4" max="4" width="12" customWidth="1"/><col min="5" max="7" width="20" customWidth="1"/><col min="8" max="9" width="28" customWidth="1"/></cols>
  <sheetData>${rows}</sheetData>
  <dataValidations count="6">${validations}</dataValidations>
</worksheet>`;
};

const buildSimpleSheet = (name: 'Options' | 'Instructions', body: string) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <cols><col min="1" max="6" width="${name === 'Options' ? 24 : 82}" customWidth="1"/></cols>
  <sheetData>${body}</sheetData>
</worksheet>`;

const instructionsSheet = () => buildSimpleSheet('Instructions', [
  ['PFS Bulk Transaction Import Instructions'],
  ['Date format must be YYYY-MM-DD.'],
  ['Allowed Type values are Expense, Income, Investment.'],
  ['Category should match your PFS dropdown master. The template includes all current category masters in one dropdown.'],
  ['Payment Mode should match your PFS PaymentMode master.'],
  ['Account should match an active account name exactly. Leave Account blank for Unassigned.'],
  ['Tax Tag is optional and helps future ITR-ready personal finance reports.'],
  ['Amount must be greater than 0.'],
  ['Do not change column headers on the Transactions sheet.'],
  ['Upload only after reviewing the preview in PFS.'],
  ['Download a fresh template after adding new categories, payment modes, accounts, or contacts.']
].map((line, index) => rowXml(index + 1, line, index === 0)).join(''));

const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/><color rgb="FFFFFFFF"/></font></fonts>
  <fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill><fill><patternFill patternType="solid"><fgColor rgb="FF0D7F73"/><bgColor indexed="64"/></patternFill></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="4"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1"/><xf numFmtId="14" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/><xf numFmtId="4" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1"/></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const blobPart = (bytes: Uint8Array) => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;

export function buildTransactionTemplateXlsx(input: XlsxTemplateInput) {
  const unique = (items: string[]) => Array.from(new Set(items.map((item) => item.trim()).filter(Boolean)));
  const cleanInput = {
    ...input,
    types: unique(input.types),
    categories: unique(input.categories),
    paymentModes: unique(input.paymentModes),
    accounts: unique(input.accounts),
    contacts: unique(input.contacts),
    taxTags: unique(input.taxTags)
  };
  return makeZip(workbookFiles([
    { name: 'Transactions', xml: buildTransactionsSheet(cleanInput) },
    { name: 'Options', xml: buildSimpleSheet('Options', optionRows(cleanInput)) },
    { name: 'Instructions', xml: instructionsSheet() }
  ], styles));
}

const getText = (node: Element, selector: string) => node.querySelector(selector)?.textContent || '';
const decodeCell = (cell: Element, sharedStrings: string[]) => {
  const type = cell.getAttribute('t');
  if (type === 'inlineStr') return getText(cell, 'is t');
  const value = getText(cell, 'v');
  if (type === 's') return sharedStrings[Number(value)] || '';
  return value;
};

const cellIndex = (ref: string) => {
  const letters = (ref.match(/[A-Z]+/)?.[0] || 'A').split('');
  return letters.reduce((total, char) => total * 26 + char.charCodeAt(0) - 64, 0) - 1;
};

const inflateRaw = async (bytes: Uint8Array) => {
  if (!('DecompressionStream' in globalThis)) throw new Error('This browser cannot read compressed XLSX files. Please use the generated template or CSV fallback.');
  const stream = new Blob([blobPart(bytes)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
};

async function unzipXlsx(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  let end = -1;
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 65558); i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) { end = i; break; }
  }
  if (end < 0) throw new Error('Invalid XLSX file.');
  const count = view.getUint16(end + 10, true);
  let offset = view.getUint32(end + 16, true);
  const files: Record<string, string> = {};
  for (let i = 0; i < count; i += 1) {
    if (view.getUint32(offset, true) !== 0x02014b50) throw new Error('Invalid XLSX central directory.');
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const fileNameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(bytes.slice(offset + 46, offset + 46 + fileNameLength));
    const localNameLength = view.getUint16(localOffset + 26, true);
    const localExtraLength = view.getUint16(localOffset + 28, true);
    const start = localOffset + 30 + localNameLength + localExtraLength;
    const compressed = bytes.slice(start, start + compressedSize);
    const content = method === 0 ? compressed : method === 8 ? await inflateRaw(compressed) : undefined;
    if (content) files[name] = decoder.decode(content);
    offset += 46 + fileNameLength + extraLength + commentLength;
  }
  return files;
}

export async function parseTransactionXlsx(file: File): Promise<XlsxRow[]> {
  const files = await unzipXlsx(await file.arrayBuffer());
  const sheetXml = files['xl/worksheets/sheet1.xml'];
  if (!sheetXml) throw new Error('Transactions sheet was not found in the XLSX file.');
  const sharedXml = files['xl/sharedStrings.xml'] || '';
  const parser = new DOMParser();
  const sharedDoc = parser.parseFromString(sharedXml || '<sst/>', 'application/xml');
  const sharedStrings = Array.from(sharedDoc.querySelectorAll('si')).map((si) => Array.from(si.querySelectorAll('t')).map((t) => t.textContent || '').join(''));
  const sheetDoc = parser.parseFromString(sheetXml, 'application/xml');
  const rows = Array.from(sheetDoc.querySelectorAll('sheetData row')).map((row) => {
    const cells: string[] = [];
    Array.from(row.querySelectorAll('c')).forEach((cell) => {
      cells[cellIndex(cell.getAttribute('r') || 'A1')] = decodeCell(cell, sharedStrings);
    });
    return cells;
  }).filter((row) => row.some(Boolean));
  const headers = rows.shift()?.map((cell) => String(cell || '').trim()) || [];
  return rows.map((row) => Object.fromEntries(headers.map((header, index) => [header, String(row[index] || '').trim()])));
}
